<template>
    <div
        :class="[
            `tw-flex tw-items-start tw-justify-between tw-rounded-md tw-bg-white night:tw-bg-neutral-800 tw-py-4 tw-flex-col sm:tw-flex-row sm:tw-items-center tw-p-4 tw-gap-4`,
        ]"
    >
        <div class="tw-flex tw-items-center tw-justify-center">
            <div class="tw-pr-4">
                <avatar
                    :fullname="props.user.fullName"
                    :color="getColour(props.user.fullName)"
                />
            </div>

            <div>
                <!-- name -->
                <p
                    class="tw-mb-0 tw-text-base tw-font-medium tw-text-textPrimary night:tw-text-white"
                >
                    <template v-if="isUserSelf">{{
                        $t('message.myPackages')
                    }}</template>
                    <template v-else>{{ props.user.fullName }}</template>
                </p>

                <p
                    class="tw-mb-0 tw-text-sm tw-text-textSecondary night:tw-text-gray-200"
                    v-if="packageCount > 0"
                >
                    {{ $t('message.usablePackages', { count: packageCount }) }}
                </p>
                <p class="tw-mb-0 tw-text-sm tw-text-error" v-else>
                    <span>{{ $t('message.packagesNotAvailable') }}</span>
                    <span>{{ $t('message.pleasePurchase') }}</span>
                </p>
            </div>
        </div>

        <SelectPackage
            :allMemberships="eligibleUnowned"
            :myMemberships="filteredMyMemberships"
            :selectedUserPkg="props.selectedUserPkg"
            @onSelectPackage="updateSelectedPackage"
            :hideHint="props.hideHint"
            :dropdownHintType="props.bookingType"
            :studioClass="props.studioClass"
            :disableBuyPackage="
                props.bookingType === 'group' || props.bookingType === 'family'
            "
        />
    </div>
</template>

<script setup lang="ts">
import SelectPackage from '@/components/bookclass/SelectPackage.vue'
import {
    type BookingType,
    type Package,
    type StudioClass,
    type User,
} from './types/types'
import Avatar from 'vue-avatar-component'
import { getColour } from '@/util/profile'
import { useStore } from '@/util/vuex'
import { AUTHENTICATION_MODULE_NAME } from '@/store/moduleNames'
import { computed } from 'vue'
import { useStudioStore } from './stores/studioStore'
import _ from 'lodash'
import {
    nowMoment,
    timestampToMoment,
    timeStampToMomentSafe,
} from '../../util/datetimets.js'
import type { Moment } from 'moment-timezone'
const props = defineProps<{
    allMemberships: Package[]
    myMemberships: Package[]
    selectedUserPkg: Package | undefined
    user: User
    bookingType?: BookingType
    hideHint?: boolean
    studioClass: StudioClass
}>()

const store = useStore()

const emit = defineEmits<{
    (e: 'onSelectPackage', pkg: Package, isMyPackage: boolean): void
}>()

const updateSelectedPackage = (
    selectedPackage: Package,
    isMyPackage: boolean
) => {
    emit('onSelectPackage', selectedPackage, isMyPackage)
}

const currentUser = computed(
    () => store.getters[`${AUTHENTICATION_MODULE_NAME}/getUser`]
)
const isUserSelf = computed(() => {
    return currentUser.value.id === props.user.id
})
const { studio } = useStudioStore()
function getPkgExpiryDate(pkg: Package): Moment {
    const expiresOn = timestampToMoment(pkg.expiresOn, studio.timezone)
    const hasCycle =
        pkg.isSubscription &&
        pkg.status == 'active' &&
        _.isNumber(pkg.numCycles)

    if (!hasCycle) {
        return expiresOn
    }

    if (!pkg.numberPurchased || !pkg.duration) {
        return expiresOn
    }

    const numCycles = pkg.numCycles ?? 0
    const numCharged = pkg.numberPurchased ?? 0

    const diff = numCycles - numCharged
    if (diff <= 0) {
        return expiresOn
    }

    const numDays = diff * (pkg.duration ?? 0)
    return expiresOn?.add(numDays, 'days')
}
function isPackageEligible(
    studioClass: StudioClass,
    pkg: Package | null
): {
    isEligible: boolean
    debugMessage: string
} {
    if (!pkg) {
        return {
            isEligible: false,
            debugMessage: 'Package not found',
        }
    }

    let numCredits = 1
    if (_.isNumber(studioClass.numCredits)) {
        numCredits = studioClass.numCredits
    }
    if (pkg.classesLeft < numCredits) {
        return {
            isEligible: false,
            debugMessage: 'Not enough credits',
        }
    }

    const hasClassRestriction =
        pkg?.hasClassRestriction && pkg?.classRestrictions?.length > 0
    if (hasClassRestriction) {
        const isPkgForAllClass =
            pkg?.classRestrictions?.includes('All') ||
            pkg?.classRestrictions?.includes('none')

        const isPkgAllowForClass = pkg?.classRestrictions?.includes(
            studioClass.name
        )

        const isAllowed = isPkgForAllClass || isPkgAllowForClass

        if (!isAllowed) {
            return {
                isEligible: false,
                debugMessage: 'Package not allowed for class',
            }
        }
    }

    if (pkg.userId) {
        const packageStartTime = pkg.startDate
        const pkgStartTimeMoment = timeStampToMomentSafe(
            packageStartTime,
            studio.timezone
        )

        const classStartTimeMoment = timestampToMoment(
            studioClass.startTimestamp,
            studio.timezone
        )

        const isClassInThePast =
            pkgStartTimeMoment &&
            pkgStartTimeMoment.isAfter(classStartTimeMoment)

        if (isClassInThePast) {
            return {
                isEligible: false,
                debugMessage: 'Package not yet started for this class',
            }
        }
        const pkgExpiryDate = getPkgExpiryDate(pkg)
        const dateToCompare =
            pkg.expiryCriterion === 'booking-date'
                ? nowMoment(studio.timezone)
                : classStartTimeMoment

        const isPkgExpired = pkgExpiryDate.isBefore(dateToCompare)
        if (isPkgExpired) {
            return {
                isEligible: false,
                debugMessage: 'Package expired',
            }
        }
    }

    const hasLocationRestriction =
        pkg?.hasLocationRestriction && pkg?.locationRestrictions?.length > 0

    if (hasLocationRestriction) {
        const isAllLocations =
            pkg.locationRestrictions.includes('All') ||
            pkg.locationRestrictions.includes('none')

        const isMatchLocation = pkg.locationRestrictions.includes(
            studioClass.location.name
        )

        const isAllowed = isAllLocations || isMatchLocation
        if (!isAllowed) {
            return {
                isEligible: false,
                debugMessage: 'Package not allowed for location',
            }
        }
    }

    return {
        isEligible: true,
        debugMessage: '',
    }
}

const filteredMyMemberships = computed(() => {
    return props.myMemberships
        .filter((pkg) => {
            const { isEligible } = isPackageEligible(props.studioClass, pkg)
            return isEligible
        })
        .sort((a, b) => a?.expiresOn?._seconds - b?.expiresOn?._seconds)
})
const eligibleUnowned = computed(() => {
    const allPkgs = (props.allMemberships || []).filter((pkg) => {
        const { isEligible } = isPackageEligible(props.studioClass, pkg)
        return isEligible
    })
    return allPkgs
})
const packageCount = computed(() => {
    return filteredMyMemberships.value?.length ?? 0
})
</script>
